import React, { useEffect, useState } from 'react';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  styled,
  Typography
} from '@mui/material';

import {
  type ComponentHealth,
  healthApi,
  type HealthStatus,
  type HealthStatusResponse
} from 'Api/health.api';
import { AdminLoginModal } from 'Components/AdminLoginModal';

import {
  ComputerIcon as ServerIcon,
  ExpandMoreIcon,
  LanguageIcon as GlobeIcon,
  MemoryIcon as CpuIcon,
  RefreshIcon,
  ScheduleIcon as ClockIcon,
  StorageIcon as DatabaseIcon,
  StorageIcon as HardDriveIcon,
  TimelineIcon as ActivityIcon
} from '@libs/shared/ui/icons';

const Container = styled(Box)`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
`;

const StatusIndicator = styled(Box)<{ status: HealthStatus }>`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
  background-color: ${({ status }) => {
    switch (status) {
      case 'Healthy':
        return '#4caf50';
      case 'Degraded':
        return '#ff9800';
      case 'Unhealthy':
        return '#f44336';
      default:
        return '#9e9e9e';
    }
  }};
`;

const QuickStatCard = styled(Card)`
  padding: 16px;
  text-align: center;
  margin: 8px;
  flex: 1;
  min-width: 200px;
`;

const ComponentCard = styled(Card)`
  margin: 8px;
  flex: 1;
  min-width: 300px;
`;

const StatsContainer = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  margin: -8px;
`;

const ComponentsContainer = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  margin: -8px;
`;

type Props = Readonly<{}>;

export const HealthStatusPage = (props: Props) => {
  const [healthData, setHealthData] = useState<HealthStatusResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [showLoginModal, setShowLoginModal] = useState<boolean>(false);

  useEffect(() => {
    const checkAuthentication = () => {
      const authenticated = sessionStorage.getItem('adminAuthenticated') === 'true';
      const authTime = sessionStorage.getItem('adminAuthTime');

      if (authenticated && authTime) {
        const authTimestamp = parseInt(authTime, 10);
        const now = Date.now();
        const twoHours = 2 * 60 * 60 * 1000;

        if (now - authTimestamp > twoHours) {
          sessionStorage.removeItem('adminAuthenticated');
          sessionStorage.removeItem('adminAuthTime');
          setIsAuthenticated(false);
          setShowLoginModal(true);
          return;
        }
      }

      setIsAuthenticated(authenticated);

      if (!authenticated) {
        setShowLoginModal(true);
      }
    };

    checkAuthentication();
  }, []);

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
    setShowLoginModal(false);
    fetchHealthStatus();
  };

  const handleLoginCancel = () => {
    window.location.href = '/';
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuthenticated');
    sessionStorage.removeItem('adminAuthTime');
    setIsAuthenticated(false);
    setShowLoginModal(true);
    setHealthData(null);
  };

  const fetchHealthStatus = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const result = await healthApi.getHealthStatus();
      if (result.isSuccess && result.data) {
        setHealthData(result.data);
        setLastRefresh(new Date());
      } else {
        console.error('Failed to fetch health status');
      }
    } catch (error) {
      console.error('Health status fetch error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchHealthStatus();
  }, []);

  const handleRefresh = (): void => {
    fetchHealthStatus();
  };

  const getStatusColor = (status: HealthStatus): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'Healthy':
        return 'success';
      case 'Degraded':
        return 'warning';
      case 'Unhealthy':
        return 'error';
      default:
        return 'default';
    }
  };

  const getComponentIcon = (componentName: string) => {
    const iconProps = { style: { fontSize: 20 } };

    if (componentName.includes('database')) {
      return <DatabaseIcon {...iconProps} />;
    }
    if (componentName.includes('api') || componentName.includes('dadesktop')) {
      return <GlobeIcon {...iconProps} />;
    }
    if (componentName.includes('memory')) {
      return <CpuIcon {...iconProps} />;
    }
    if (componentName.includes('disk')) {
      return <HardDriveIcon {...iconProps} />;
    }
    return <ActivityIcon {...iconProps} />;
  };

  const formatTimestamp = (timestamp: string): string => {
    return new Date(timestamp).toLocaleString();
  };

  const renderComponentCard = (name: string, component: ComponentHealth) => (
    <ComponentCard key={name}>
      <CardContent>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          {getComponentIcon(name)}
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {name.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </Typography>
          <Chip
            label={`${healthApi.getStatusIcon(component.status)} ${component.status}`}
            color={getStatusColor(component.status)}
            size="small"
          />
        </Box>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          {component.description}
        </Typography>

        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <ClockIcon style={{ fontSize: 16 }} />
          <Typography variant="caption">{healthApi.formatDuration(component.duration)}</Typography>
        </Box>

        {component.exception && (
          <Box mt={1} p={1} bgcolor="error.light" borderRadius={1}>
            <Typography variant="caption" color="error.dark">
              <strong>Error:</strong> {component.exception}
            </Typography>
          </Box>
        )}

        {(() => {
          const componentData = component.data;
          if (componentData == null || Object.keys(componentData).length === 0) {
            return null;
          }
          return (
            <Accordion sx={{ mt: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="caption">View Details</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  {Object.entries(componentData).map(([key, value]) => (
                    <Box key={key} display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography variant="caption" component="span" fontWeight="bold">
                        {key}:
                      </Typography>
                      <Typography variant="caption" component="span">
                        {String(value)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })()}
      </CardContent>
    </ComponentCard>
  );

  const renderSystemInfoCard = () => {
    if (!healthData?.systemInfo) {
      return null;
    }

    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <ServerIcon />
            <Typography variant="h6">System Information</Typography>
          </Box>
          <Box>
            {Object.entries(healthData.systemInfo).map(([key, value]) => (
              <Box
                key={key}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Typography variant="body2" color="text.secondary">
                  {key.replace(/([A-Z])/g, ' $1').trim()}:
                </Typography>
                <Typography variant="body2" fontFamily="monospace">
                  {String(value)}
                </Typography>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>
    );
  };

  if (isLoading && !healthData) {
    return (
      <Container>
        <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px">
          <Box display="flex" alignItems="center" gap={2}>
            <CircularProgress />
            <Typography>Loading health status...</Typography>
          </Box>
        </Box>
      </Container>
    );
  }

  if (!healthData) {
    return (
      <Container>
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography color="text.secondary" gutterBottom>
              Failed to load health status
            </Typography>
            <Button onClick={handleRefresh} variant="contained" startIcon={<RefreshIcon />}>
              Retry
            </Button>
          </CardContent>
        </Card>
      </Container>
    );
  }

  const componentEntries = Object.entries(healthData.components);

  if (!isAuthenticated) {
    return (
      <Container>
        <AdminLoginModal
          open={showLoginModal}
          onSuccess={handleLoginSuccess}
          onCancel={handleLoginCancel}
        />
      </Container>
    );
  }

  return (
    <Container>
      <Box mb={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h3" component="h1" gutterBottom>
              System Health Status
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Real-time monitoring of system components
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <Button
              onClick={handleRefresh}
              disabled={isLoading}
              variant="outlined"
              startIcon={<RefreshIcon />}
            >
              Refresh
            </Button>
            <Button variant="outlined" color="secondary" onClick={handleLogout}>
              Logout
            </Button>
          </Box>
        </Box>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center" gap={2}>
                <StatusIndicator status={healthData.status}>
                  {healthApi.getStatusIcon(healthData.status)}
                </StatusIndicator>
                <Box>
                  <Typography variant="h4" component="h2">
                    System is {healthData.status}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Last checked: {formatTimestamp(healthData.timestamp)}
                  </Typography>
                </Box>
              </Box>
              <Box textAlign="right">
                <Typography variant="body2" color="text.secondary">
                  Total Check Duration
                </Typography>
                <Typography variant="h6" fontFamily="monospace">
                  {healthApi.formatDuration(healthData.totalDuration)}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <StatsContainer sx={{ mb: 3 }}>
          <QuickStatCard>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Version
                </Typography>
                <Typography variant="h6">{healthData.version}</Typography>
              </Box>
              <ServerIcon color="action" />
            </Box>
          </QuickStatCard>

          <QuickStatCard>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Environment
                </Typography>
                <Typography variant="h6">{healthData.environment}</Typography>
              </Box>
              <GlobeIcon color="action" />
            </Box>
          </QuickStatCard>

          <QuickStatCard>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Components
                </Typography>
                <Typography variant="h6">{componentEntries.length}</Typography>
              </Box>
              <ActivityIcon color="action" />
            </Box>
          </QuickStatCard>

          <QuickStatCard>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Last Refresh
                </Typography>
                <Typography variant="h6">{lastRefresh.toLocaleTimeString()}</Typography>
              </Box>
              <ClockIcon color="action" />
            </Box>
          </QuickStatCard>
        </StatsContainer>

        <Box mb={3}>
          <Typography variant="h5" component="h3" gutterBottom>
            Component Health
          </Typography>
          <ComponentsContainer>
            {componentEntries.map(([name, component]) => renderComponentCard(name, component))}
          </ComponentsContainer>
        </Box>

        <Divider sx={{ my: 3 }} />

        {renderSystemInfoCard()}
      </Box>
    </Container>
  );
};
